import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { setCurrentEmms } from "../../redux/slices/currentEmmsSlice";
import { setEmmsHeaders } from "../../redux/slices/emmsAuthSlice";
import TitleWithDropdown from "../TitleWithDropdown";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  Pagination,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import StayCurrentPortraitIcon from "@mui/icons-material/StayCurrentPortrait";
import StayCurrentLandscapeIcon from "@mui/icons-material/StayCurrentLandscape";
import image from "../../logos/templates.png";
import "../../components/CommonDashboard.css";


export const STUDIO_STORAGE_KEY = "kioskStudioItems";

const DEFAULT_BG = "linear-gradient(160deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)";
const DEFAULT_ACCENT = "#64748b";

export function KioskMiniPreview({ template, scale = 1.2, orientation = "portrait" }) {
  const isLandscape = orientation === "landscape";
  const W = isLandscape ? 230 * scale : 130 * scale;
  const H = isLandscape ? 130 * scale : 230 * scale;
  const fs = 5.5 * scale;

  const bg = template.bgGradient || DEFAULT_BG;
  const accent = template.accentColor || DEFAULT_ACCENT;
  const elements = template.previewElements || [
    { type: "header", label: template.name || "Template", color: "#fff", top: "10%", height: "10%" },
    { type: "bar", label: template.category || (template.tags && template.tags[0]) || "Kiosk", top: "30%", height: "7%", color: accent },
  ];

  return (
    <div
      style={{
        width: W,
        height: H,
        borderRadius: 6 * scale,
        background: bg,
        position: "relative",
        overflow: "hidden",
        flexShrink: 0,
        border: `1px solid ${accent}44`,
        boxShadow: `0 2px 8px rgba(0,0,0,0.18)`,
      }}
    >
      {elements.map((el, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: "8%",
            width: "84%",
            top: el.top,
            height: el.height,
            background: el.color,
            borderRadius: 3 * scale,
            display: "flex",
            alignItems: "center",
            justifyContent: el.type === "image" || el.type === "map" ? "center" : "flex-start",
            paddingLeft: el.type === "image" || el.type === "map" ? 0 : 6 * scale,
            overflow: "hidden",
          }}
        >
          {el.type === "image" || el.type === "map" ? (
            <span style={{ fontSize: 26 * scale }}>{el.label}</span>
          ) : (
            <span
              style={{
                fontSize: fs,
                color: el.type === "header" || el.type === "subheader" ? el.color : "#ffffffcc",
                fontWeight: el.type === "header" ? 700 : el.type === "subheader" ? 600 : 400,
                letterSpacing: el.type === "header" ? "0.06em" : 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
              }}
            >
              {el.label}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function TemplateCard({ template, onSelect, orientation }) {
  const cardWidth = orientation === "landscape" ? 270 : 190;
  const category = template.category || (template.tags && template.tags[0]) || "";
  return (
    <Card
      elevation={2}
      onClick={() => onSelect(template)}
      sx={{
        width: cardWidth,
        borderRadius: 2,
        cursor: "pointer",
        transition: "box-shadow 0.2s, width 0.2s",
        "&:hover": { boxShadow: 6 },
      }}
    >
      <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", pb: 1.5, pt: 1.5 }}>
        <KioskMiniPreview template={template} orientation={orientation} />
        <Typography variant="body2" fontWeight={700} mt={1} noWrap sx={{ maxWidth: cardWidth - 30 }}>
          {template.name}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
          {category && <Chip label={category} size="small" sx={{ fontSize: 10, height: 18 }} />}
          {template.price > 0 && (
            <Chip label={`$${template.price}`} size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function KioskTemplatesDashboard() {
  const dispatch = useDispatch();
  const api = localStorage.getItem("api");
  const headers = useSelector((state) => state.header);
  const emmsheaders = useSelector((state) => state.emmsheader);
  const emms = useSelector((state) => state.emms);

  const [selectedOption, setSelectedOption] = useState(emms?.emms[0]?.id);
  const [storeItems, setStoreItems] = useState([]);
  const [ownedStoreIds, setOwnedStoreIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [copiedIds, setCopiedIds] = useState({});
  const [orientation, setOrientation] = useState("portrait");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);

  const perPage = orientation === "landscape" ? 5 : 6;

  const fetchStoreItems = (value, nameFilter, orientFilter) => {
    const selectedEmms = emms?.emms?.find((element) => element.id === value);
    if (!selectedEmms) return;

    const localEmmsHeaders = {
      headers: {
        "Content-Type": "application/json",
        "x-api-id": selectedEmms?.apiId,
        "x-api-key": selectedEmms?.apiKey,
      },
    };
    dispatch(setCurrentEmms(selectedEmms));
    dispatch(setEmmsHeaders(localEmmsHeaders));

    const query = {};
    if (nameFilter) query.name = nameFilter;
    if (orientFilter) query.orientation = orientFilter;
    const requestBody = {
      path: "kioskStore",
      query,
      emmsId: value,
      body: {},
      requestType: "GET",
      headers: localEmmsHeaders.headers,
    };
    setLoading(true);
    axios
      .post(api + `/emms/piggyback`, requestBody, headers)
      .then((response) => {
        setStoreItems(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error("Failed to fetch kiosk store:", error);
        setStoreItems([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchOwnedStoreIds(selectedOption);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchStoreItems(selectedOption, search, orientation);
      setPage(1);
    }, 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption, search, orientation]);

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
    fetchOwnedStoreIds(event.target.value);
    fetchStoreItems(event.target.value, search, orientation);
  };

  const fetchOwnedStoreIds = (value) => {
    const selectedEmms = emms?.emms?.find((element) => element.id === value);
    if (!selectedEmms) return;
    const requestBody = {
      path: "kioskStudio",
      query: {},
      emmsId: value,
      body: {},
      requestType: "GET",
      headers: { "Content-Type": "application/json", "x-api-id": selectedEmms.apiId, "x-api-key": selectedEmms.apiKey },
    };
    axios
      .post(api + `/emms/piggyback`, requestBody, headers)
      .then((res) => {
        const items = Array.isArray(res.data) ? res.data : [];
        const ids = new Set(items.map((i) => i.kioskStoreId).filter(Boolean));
        setOwnedStoreIds(ids);
      })
      .catch(() => {});
  };

  const options = emms?.emms?.map((emm) => ({
    label: emm.name,
    value: emm.id,
  }));

  const handleCopy = (template) => {
    const requestBody = {
      path: "kioskStore/purchase",
      query: {},
      emmsId: `${selectedOption}`,
      body: { storeId: template.id },
      requestType: "POST",
      headers: emmsheaders?.emmsheaders?.headers,
    };
    axios
      .post(api + `/emms/piggyback`, requestBody, headers)
      .then(() => {
        setCopiedIds((prev) => ({ ...prev, [template.id]: true }));
        setOwnedStoreIds((prev) => new Set([...prev, template.id]));
        setSelected(null);
      })
      .catch((error) => {
        console.error("Purchase failed:", error);
      });
  };

  const handlePreview = (template) => {
    if (template.screens) {
      localStorage.setItem(
        "kioskPreviewData",
        JSON.stringify({ screens: template.screens, dataSources: template.dataSources || [] })
      );
    }
    window.open("/kiosk-preview", "_blank");
  };

  const visibleItems = storeItems.filter((t) => !ownedStoreIds.has(t.id));
  const pageCount = Math.ceil(visibleItems.length / perPage);
  const paginated = visibleItems.slice((page - 1) * perPage, page * perPage);

  const category = selected
    ? selected.category || (selected.tags && selected.tags[0]) || ""
    : "";

  return (
    <div className="mainContainer">
      <header style={{ marginLeft: "10%" }}>
        <figure>
          <img className="img" src={image} alt="Kiosk Templates" />
        </figure>
      </header>

      <div className="title-container">
        <div className="title">
          <TitleWithDropdown
            title="EMMS Service"
            options={options}
            selectedOption={selectedOption}
            onOptionChange={handleOptionChange}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <ToggleButtonGroup
            value={orientation}
            exclusive
            size="small"
            onChange={(_, val) => { if (val) setOrientation(val); }}
          >
            <ToggleButton value="portrait" sx={{ textTransform: "none", gap: 0.5, fontSize: 12 }}>
              <StayCurrentPortraitIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="landscape" sx={{ textTransform: "none", gap: 0.5, fontSize: 12 }}>
              <StayCurrentLandscapeIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            placeholder="Search templates…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 200 }}
          />
        </div>
      </div>

      <div style={{ marginLeft: "10%", marginTop: "2%", marginBottom: "5%" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : visibleItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {search ? `No templates match "${search}"` : "No templates available."}
          </Typography>
        ) : (
          <>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
              {paginated.map((tpl) => (
                <TemplateCard
                  key={tpl.id}
                  template={tpl}
                  onSelect={setSelected}
                  orientation={orientation}
                />
              ))}
            </Box>
            {pageCount > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </div>

      {/* ── Play Store–style detail dialog ── */}
      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selected && (
          <DialogContent sx={{ p: 0, overflow: "hidden" }}>
            {/* close button */}
            <IconButton
              onClick={() => setSelected(null)}
              size="small"
              sx={{ position: "absolute", top: 10, right: 10, zIndex: 1 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            {/* ── top section: preview + info ── */}
            <Box sx={{ display: "flex", gap: 3, p: 3, pb: 2 }}>
              {/* left — main preview */}
              <Box sx={{ flexShrink: 0 }}>
                <KioskMiniPreview template={selected} scale={1.4} orientation={orientation} />
              </Box>

              {/* right — meta + actions */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
                  {selected.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  {selected.developer || selected.owner || ""}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                  {category && <Chip label={category} size="small" />}
                  {selected.screens && (
                    <Chip
                      label={`${selected.screens} screen${selected.screens !== 1 ? "s" : ""}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {selected.orientation && (
                    <Chip label={selected.orientation} size="small" variant="outlined" />
                  )}
                  {selected.price > 0 ? (
                    <Chip label={`$${selected.price}`} size="small" color="primary" variant="outlined" />
                  ) : (
                    <Chip label="Free" size="small" color="success" variant="outlined" />
                  )}
                </Box>

                {/* action buttons */}
                <Box sx={{ display: "flex", gap: 1.5, mt: "auto", pt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handlePreview(selected)}
                    sx={{ textTransform: "none" }}
                  >
                    Preview
                  </Button>
                  {selected.price > 0 ? (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<ShoppingCartIcon />}
                      onClick={() => handleCopy(selected)}
                      disabled={!!copiedIds[selected.id]}
                      sx={{ textTransform: "none" }}
                    >
                      {copiedIds[selected.id] ? "Purchased" : `Buy — $${selected.price}`}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={copiedIds[selected.id] ? <CheckCircleIcon /> : <ContentCopyIcon />}
                      onClick={() => handleCopy(selected)}
                      disabled={!!copiedIds[selected.id]}
                      sx={{ textTransform: "none" }}
                    >
                      {copiedIds[selected.id] ? "Added" : "Copy"}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* ── description ── */}
            <Box sx={{ px: 3, py: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                About this template
              </Typography>
              <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                {selected.description || "No description available."}
              </Typography>
            </Box>

            <Divider />

            {/* ── screenshots strip ── */}
            <Box sx={{ px: 3, py: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                Screenshots
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  overflowX: "auto",
                  pb: 1,
                  "&::-webkit-scrollbar": { height: 4 },
                  "&::-webkit-scrollbar-thumb": { borderRadius: 2, bgcolor: "grey.300" },
                }}
              >
                <KioskMiniPreview template={selected} scale={0.9} orientation="portrait" />
                <KioskMiniPreview template={selected} scale={0.9} orientation="landscape" />
                <KioskMiniPreview template={selected} scale={0.9} orientation="portrait" />
                <KioskMiniPreview template={selected} scale={0.9} orientation="landscape" />
              </Box>
            </Box>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
